import withHandlers from 'recompose/withHandlers';
import { connect } from 'react-redux';
import { branch, compose, lifecycle, renderNothing, withState } from 'recompose';
import {
  disconnectFromQuickBooks,
  fetchQuickBooksSettings,
  fetchQuickBooksSettingsAction,
  storeQuickBooksAccountMap,
} from '../../actions';
import withToggle from '../../../../../common/hoc/withToggle';
import withOAuthHandler from '../../../../../common/hoc/withOAuthHandler';
import { activeShop, getIsRentalBarber, getActiveBarberId } from '../../../../../auth/selectors';
import {
  quickBooksAuthorizationUrl,
  quickBooksBankAccountTypes,
  quickBooksBankDepositAccounts,
  quickBooksIsConnected,
} from '../../selectors';
import QuickBooksSettings from '../components/QuickBooksSettings';

const DEFAULT_ACCOUNT_MAP = {
  bankAccountType: null,
  discountsAccount: null,
  promosAccount: null,
  refundsAccount: null,
  servicesAccount: null,
  servicesTaxAccount: null,
  productsAccount: null,
  productsTaxAccount: null,
  giftCardsAccount: null,
  feesAccount: null,
  financingPaydownAccount: null,
  tipsAccount: null,
  cashAccount: null,
};

const QuickBooksSettingsContainer = compose(
  connect(
    state => ({
      shop: activeShop(state),
      barberId: getActiveBarberId(state),
      isRentalBarber: getIsRentalBarber(state),
      isConnected: quickBooksIsConnected(state),
      authUrl: quickBooksAuthorizationUrl(state),
      bankAccountTypes: quickBooksBankAccountTypes(state),
      depositAccounts: quickBooksBankDepositAccounts(state),
    }),
    { fetchQuickBooksSettingsAction, fetchQuickBooksSettings, storeQuickBooksAccountMap, disconnectFromQuickBooks },
  ),
  branch(
    props => !props.shop.useQuickBooks,
    renderNothing,
  ),
  withState('accountMap', 'setAccountMap', DEFAULT_ACCOUNT_MAP),
  withState('submitting', 'setSubmitting', false),
  withState('valid', 'setValid', false),
  withState('connectionButtonText', 'setConnectionButtonText', 'Connect to QuickBooks'),
  withToggle('quickBooks'),
  withOAuthHandler,
  withHandlers({
    connect: props => () => {
      if (!props.isConnected) {
        // Launch Authorization popup
        props.authorize(props.authUrl, 'quick-books', () => {
          let type = 'shop';
          let id = props.shop.id;
          if (props.isRentalBarber) {
            type = 'barber';
            id = props.barberId;
          }
          props.fetchQuickBooksSettings(type, id, () => { props.quickBooksOn(); });
        }, {
          size: { width: 1160 },
        });
      } else {
        props.quickBooksOn();
      }
    },
    disconnect: props => () => {
      props.setSubmitting(true);
      props.disconnectFromQuickBooks(props.shop.id).then(() => {
        props.setSubmitting(false);
        props.quickBooksOff();
        props.setAccountMap(DEFAULT_ACCOUNT_MAP);
        props.addNotification({
          title: 'Success',
          level: 'success',
          message: 'You successfully disconnected your Quickbooks account.',
        });
      }).catch(() => {
        props.setSubmitting(false);
        props.addNotification({
          title: 'Error occured',
          message: 'Something went wrong. Please try again.',
        });
      });
    },
    onSubmit: props => (e) => {
      e.preventDefault();
      props.setSubmitting(true);
      return props.storeQuickBooksAccountMap(props.shop.id, props.accountMap).then(() => {
        props.setSubmitting(false);
        props.quickBooksOff();
        props.addNotification({
          title: 'Success',
          level: 'success',
          message: 'Quickbooks accounts map saved successfully!',
        });
      }).catch(() => {
        props.setSubmitting(false);
        props.addNotification({
          title: 'Error occured',
          message: 'Something went wrong. Please check all fields and re-submit.',
        });
      });
    },
    onSelectAccount: props => (accountType, account) => {
      const isAccountSelected = account !== null;
      if (accountType === 'bankAccountType') {
        props.setValid(isAccountSelected);
      }
      props.setAccountMap({
        ...props.accountMap,
        [accountType]: isAccountSelected ? account.id : null,
      });
    },
  }),
  lifecycle({
    componentWillMount() {
      let type = 'shop';
      let id = this.props.shop.id;
      if (this.props.isRentalBarber) {
        type = 'barber';
        id = this.props.barberId;
      }
      this.props.fetchQuickBooksSettings(type, id, (settings) => {
        this.props.setAccountMap(settings.map || DEFAULT_ACCOUNT_MAP);
        this.props.setValid(settings.map !== null && settings.map.bankAccountType !== null);
      }).catch((e) => {
        if (e.settings) {
          this.props.setConnectionButtonText('Re-connect QuickBooks account');
          this.props.fetchQuickBooksSettingsAction(e.settings);
        }
      });
    },
  }),
)(QuickBooksSettings);

export default QuickBooksSettingsContainer;
